import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { postLead } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const DemoModal = ({ open, onClose }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postLead({
        name: form.name,
        topic: "demo",
        email: form.email,
        azienda: form.company,
        message: form.message,
        source: "demo",
      });
      toast.success(t.demo.success);
      setForm({ name: "", email: "", company: "", message: "" });
      onClose();
    } catch {
      toast.error(t.demo.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid="demo-modal"
            className="relative glass-panel rounded-2xl p-8 w-full max-w-md"
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35 }}
          >
            <button onClick={onClose} data-testid="demo-close-button" className="absolute top-4 right-4 text-white/50 hover:text-white" aria-label="Chiudi">
              <X size={20} />
            </button>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-2">{t.demo.overline}</p>
            <h3 className="font-display text-2xl font-bold mb-6">{t.demo.title}</h3>
            <form onSubmit={submit} className="flex flex-col gap-4">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder={t.demo.namePh}
                data-testid="demo-name-input"
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-neon-cyan/60 transition-colors"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t.demo.emailPh}
                data-testid="demo-email-input"
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-neon-cyan/60 transition-colors"
              />
              <input
                value={form.company}
                onChange={set("company")}
                placeholder={t.demo.companyPh}
                data-testid="demo-company-input"
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-neon-cyan/60 transition-colors"
              />
              <textarea
                value={form.message}
                onChange={set("message")}
                placeholder={t.demo.messagePh}
                rows={3}
                data-testid="demo-message-input"
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-neon-cyan/60 transition-colors resize-none"
              />
              <label className="flex items-start gap-2.5 text-xs text-white/60 cursor-pointer">
                <input type="checkbox" required data-testid="demo-privacy-checkbox" className="mt-0.5 accent-[#00F0FF]" />
                <span>
                  {t.demo.privacy}{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-neon-cyan underline">Privacy Policy</a>
                </span>
              </label>
              <button
                type="submit"
                disabled={sending}
                data-testid="demo-submit-button"
                className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-neon-cyan to-neon-violet px-6 py-3 font-semibold text-void hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Send size={16} /> {sending ? t.demo.sending : t.demo.send}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DemoModal;
